import React, { useRef, useEffect } from 'react';
import { IconType } from 'react-icons'; 

interface IButtonProps {
    id?: string;
    type?: 'button' | 'submit' | 'reset';
    text?: string;
    title?: string;
    buttonStyle?: string;
    size?: string;
    intent?: string;
    disabled?: boolean;
    loading?: boolean;
    reactIcon?: IconType;
    iconStyle?: React.CSSProperties;
    iconHoverColor?: 'red' | 'green' | 'blue' | 'lightblue';
    icon?: string;
    onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    style?: React.CSSProperties;
    toolTip?: string;
    className?: string;
    form?: string;
    iconImage?: string;
}

const Button = (props: IButtonProps) => {
    const buttonRef = useRef<HTMLButtonElement>(undefined);
    const disabledRef = useRef(props.disabled);

    useEffect(() => {
        disabledRef.current = props.disabled;
    }, [props.disabled]);

    const disableClickHandler = () => {
        if (buttonRef.current && buttonRef.current.form && buttonRef.current.type === 'submit') {
            buttonRef.current.form.dispatchEvent(new Event('submit', { cancelable: true }));
        }
        if (buttonRef.current) {
            buttonRef.current.disabled = true;
        }
        window.setTimeout(() => {
            if (buttonRef.current && !disabledRef.current) {
                buttonRef.current.disabled = false;
            }
        }, 500);
    };

    useEffect(() => {
        const el = buttonRef.current;
        if (el) {
            el.addEventListener('click', disableClickHandler);
        }
        return () => {
            if (el) {
                el.removeEventListener('click', disableClickHandler);
            }
        };
    }, []); // [] — button element doesn't change after mount; buttonRef.current in dep array caused re-attach on every render

    const buttonClass = `dlc-form-button-${props.buttonStyle || 'default'} ${props.size || 'normal'} ${props.intent} ${props.toolTip ? 'tooltip' : ''
        } ${props.disabled && !props.loading ? 'disabled' : ''} ${props.loading ? 'loading' : ''} ${!!props.className ? props.className : ''}`;

    // `loading` detaches the handler just as `disabled` does. Callers reasonably read a spinner as
    // "in flight, you cannot click it again", and before this they were wrong: only `disabled`
    // cleared onClick, so a button passed just `loading` stayed live and could fire duplicate
    // requests. Note the native `disabled` attribute is still deliberately withheld while loading
    // (see below) to keep the spinner styling rather than a greyed-out button - which makes this
    // the only thing stopping the second click. The 500ms disableClickHandler above is a
    // double-click guard, not a substitute: it re-enables long before a slow request returns.
    const clickHandler = props.disabled || props.loading ? undefined : props.onClick || undefined;

    let iconElement = undefined;
    switch (props.icon) {
        case 'user-plus':
            iconElement = <i className="fas fa-user-plus" />;
            break;
        case 'trash':
            iconElement = <i className="fas fa-trash" />;
            break;
        case 'contract':
            iconElement = <i className="fas fa-file" />;
            break;
        case 'shadow':
            iconElement = <i className="fas fa-user-secret" />;
            break;
        case 'edit':
            iconElement = <i className="fas fa-edit" />;
            break;
        default:
            break;
    }

    return (
        <button
            id={!!props.id ? props.id : ''}
            className={buttonClass}
            type={props.type || 'button'}
            onClick={clickHandler}
            disabled={props.disabled && !props.loading}
            title={props.title}
            style={props.style}
            ref={buttonRef}
            form={props.form}
        >
            {props.reactIcon && 
                <props.reactIcon 
                    className={props.iconHoverColor ? `icon-button-${props.iconHoverColor}hover` : ``}
                    style={props.iconStyle}
                />}
            {iconElement || props.text}
            {props.iconImage && <img className={`icon-button-${props.iconHoverColor}hover`} style={props.iconStyle} src={props.iconImage}/>}
            {props.toolTip && <span className="tooltiptext">{props.toolTip}</span>}
            {props.loading && <span className="dlc-form-button-spinner" />}
        </button>
    );
};

export default Button;



// WEBPACK FOOTER //
// ./src/components/controls/forms/redesign/FormButton.tsx